/* cspell:words Transfered */
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useCredentialRequirementConfiguration } from "@src/appV2/redesign/Documents/useCredentialRequirementConfiguration";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { getBlockingRequirementsContext } from "@src/appV2/redesign/Shift/Bookability/Documents/getBlockingRequirementsContext";
import { ShiftBottomSheetContent } from "@src/appV2/redesign/Shift/BottomSheet/SheetContent";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import { convertShiftToSlotId } from "@src/appV2/redesign/Shift/Open/useGetShiftsSlotV2";
import { useGetModalShiftData } from "@src/appV2/redesign/ShiftDiscovery/useGetModalShiftData";
import {
  Divider,
  LoadingSpinner,
  Text,
  Title,
  useShake,
} from "@src/appV2/redesign/system/ui-components";
import { formatDayOfMonthWithDayName } from "@src/appV2/redesign/utils/formatDayOfMonthWithDayName";
import { useGetShift } from "@src/appV2/Shifts/Shift/api/useGetShift";
import { parseISO } from "date-fns";
import { useRef } from "react";

import { InstantPayIndicator } from "../../components/InstantPayIndicator";
import { TransferedToMeInfo } from "./TransferedToMeInfo";

export interface ShiftDetailInfoProps {
  shiftId: string;
}

export function ShiftDetailInfo(props: ShiftDetailInfoProps) {
  const { shiftId } = props;

  const { data: dataGetShift, isLoading: isLoadingGetShift } = useGetShift(shiftId);
  const payBreakdown = dataGetShift
    ? getFormattedShiftPayBreakdown(
        dataGetShift.response.offer?.pay.value,
        dataGetShift.response.finalPay,
        dataGetShift.response.originalAmount
      )
    : undefined;

  const { data, isLoading, shiftFailedLoading } = useGetModalShiftData({
    shiftId,
  });
  const {
    shift,
    bookabilityStatus,
    hasHoursRestrictionConflict,
    blockingRequirements,
    friendsMap,
    outboundFriendRequestsSet,
    shiftSlots,
  } = data;

  const { ref: bookabilityCardsShakeableRef } = useShake();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  if (isLoading || isLoadingGetShift) {
    return (
      <Stack alignItems="center">
        <LoadingSpinner size="large" variant="primary" />
      </Stack>
    );
  }

  if (!payBreakdown || !shift || !bookabilityStatus) {
    return null;
  }

  const { attributes: shiftAttributes, workplace } = shift;

  return (
    <Stack sx={{ paddingLeft: 3 }} spacing={4}>
      <Text semibold variant="body1">
        {shiftAttributes.qualification}
      </Text>

      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Title variant="h3" component="h2">
          {formatDayOfMonthWithDayName(parseISO(shiftAttributes.start), {
            formatDayString: "EEEE",
            timezone: workplace.attributes.location.timezone,
          })}
        </Title>
      </Stack>
      <Divider />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        <HourlyPayLabel size="large" hourlyPay={payBreakdown?.hourlyPay} />
        <Stack direction="row" spacing={2} alignItems="center">
          <TotalPayLabel size="medium" totalPay={payBreakdown?.totalPay} />
          {isDefined(shift) && shiftAttributes.isInstantPay && <InstantPayIndicator />}
        </Stack>
      </Stack>
      <Divider />

      <TransferedToMeInfo />

      <ShiftBottomSheetContent
        shiftId={shiftId}
        shiftType={shiftAttributes.type}
        shiftTimeSlot={shiftAttributes.timeSlot}
        shiftStartIso={shiftAttributes.start}
        shiftEndIso={shiftAttributes.end}
        shiftDurationInHours={shiftAttributes.durationInHours}
        workplace={workplace}
        bookabilityStatus={bookabilityStatus}
        hasHoursRestrictionConflict={hasHoursRestrictionConflict}
        shiftFailedLoading={shiftFailedLoading}
        blockingRequirementsContext={
          blockingRequirements
            ? getBlockingRequirementsContext({
                blockingRequirements,
                credentialRequirementConfiguration,
              })
            : undefined
        }
        shiftSlots={
          shiftSlots?.[
            convertShiftToSlotId({
              start: shiftAttributes.start,
              end: shiftAttributes.end,
              facilityId: workplace.id,
            })
          ]
        }
        friendsMap={friendsMap}
        outboundFriendRequestsSet={outboundFriendRequestsSet}
        shiftSpecifications={shiftAttributes.specifications}
        restrictedToWindow={shiftAttributes.windowRestriction}
        scrollContainerRef={scrollContainerRef}
        bookabilityCardsShakeableRef={bookabilityCardsShakeableRef}
        shiftQualification={shiftAttributes.qualification}
      />
    </Stack>
  );
}
